// Spec 09 §4.19: module 02's import flow on `open_sales_orders`, framed in sales
// as §4.18 frames customers. The S7 Open orders tab runs the same components.
// The gate is `onboarding.import.customers` (spec 02 §7); the server checks
// `sales.order.write` at commit and words it as the creator's 422 (D-353 ruling 3).

import { useEffect, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { HelpTip } from "../../../../components/help-tip";
import { CardSkeleton, EmptyState, ForbiddenState } from "../../../../components/states";
import { useRoleCodesFeed } from "../../../../lib/role-codes";
import { useImport } from "../../../onboarding/hooks/onboarding-queries";
import { useOnboardingPermissions } from "../../../onboarding/hooks/permissions";
import { ImportJobDetail } from "../../../onboarding/screens/import-detail";
import { type ImportKind } from "../../../onboarding/screens/imports/import-model";
import { ImportJobsTable } from "../../../onboarding/screens/imports/jobs-table";
import { ImportUploadPanel } from "../../../onboarding/screens/imports/upload-panel";
import { quoteKeys } from "../hooks/quote-queries";
import { importGate, type ImportGate } from "./customers-import";
import "../../../onboarding/screens/imports/imports.css";

/** The one lane this page runs. */
const ORDERS: ImportKind = "open_sales_orders";
const ORDER_KINDS: readonly ImportKind[] = [ORDERS];

function useOrdersImportGate(): {
  gate: ImportGate;
  canImport: (kind: ImportKind) => boolean;
} {
  const perms = useOnboardingPermissions();
  const feed = useRoleCodesFeed();
  return {
    gate: importGate({
      isResolved: perms.isResolved,
      isFeedError: feed.isFeedError,
      mayImport: perms.canImport(ORDERS),
    }),
    canImport: (kind: ImportKind) => kind === ORDERS && perms.canImport(kind),
  };
}

/** `/sales/orders/import`: upload, and the reader's open orders jobs. */
export function SalesOrdersImportScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { gate, canImport } = useOrdersImportGate();
  const open = (jobId: string) => {
    void navigate({ to: "/sales/orders/import/$importId", params: { importId: jobId } });
  };

  return (
    <section className="sales-orders-import" data-testid="sales-orders-import">
      <PageHeader
        title={t("sales.orderImport.title")}
        crumbs={[{ label: t("sales.orders.detail.crumbRoot"), to: "/sales/orders" }]}
        actions={
          <HelpTip
            label={t("nav.helpAbout", { topic: t("sales.orderImport.title") })}
            data-testid="sales-orders-import-intro"
          >
            {t("sales.orderImport.intro")}
          </HelpTip>
        }
        data-testid="sales-orders-import-head"
      />
      <OrdersImportGateBody gate={gate}>
        <div className="catalogue-import">
          <ImportUploadPanel kinds={ORDER_KINDS} canImport={canImport} onUploaded={open} />
          <ImportJobsTable
            kinds={ORDER_KINDS}
            lockedKind={ORDERS}
            canRun={canImport}
            onOpen={open}
          />
        </div>
      </OrdersImportGateBody>
    </section>
  );
}

/** `/sales/orders/import/$importId`: one open orders job, start to finish. */
export function SalesOrderImportJobScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const params = useParams({ strict: false });
  const importId = typeof params.importId === "string" ? params.importId : "";
  const { gate } = useOrdersImportGate();

  // The cache entry the job page polls. A refused reader asks for nothing.
  const job = useImport(gate === "open" ? importId : "");
  const status = job.data?.status;
  const kind = job.data?.kind;

  // The order book lists what the file loaded the moment the reader goes back.
  useEffect(() => {
    if (status === "committed" && kind === ORDERS) {
      void qc.invalidateQueries({ queryKey: quoteKeys.orderLists() });
    }
  }, [status, kind, qc]);

  return (
    <section className="sales-orders-import" data-testid="sales-orders-import-job">
      <PageHeader
        title={t("sales.orderImport.jobTitle")}
        crumbs={[
          { label: t("sales.orders.detail.crumbRoot"), to: "/sales/orders" },
          { label: t("sales.orderImport.title"), to: "/sales/orders/import" },
        ]}
        data-testid="sales-orders-import-head"
      />
      <OrdersImportGateBody gate={gate}>
        {kind !== undefined && kind !== ORDERS ? (
          // Another lane's job reached through this address is not this page's.
          <EmptyState
            title={t("onboarding.importShared.problems.notFoundTitle")}
            body={t("sales.orderImport.otherKind")}
          />
        ) : (
          <ImportJobDetail
            id={importId}
            exits={{
              back: (
                <Link
                  to="/sales/orders/import"
                  className="wizard-header-link import-back"
                  data-testid="sales-orders-import-all"
                >
                  {t("sales.orderImport.allImports")}
                </Link>
              ),
              doneLabel: t("sales.orderImport.back"),
              onDone: () => {
                void navigate({ to: "/sales/orders" });
              },
            }}
          />
        )}
      </OrdersImportGateBody>
    </section>
  );
}

/** The gate's three faces around the page's body. */
function OrdersImportGateBody({ gate, children }: { gate: ImportGate; children: ReactNode }) {
  const { t } = useTranslation();
  if (gate === "pending") {
    return <CardSkeleton lines={5} label={t("sales.orderImport.loading")} />;
  }
  if (gate === "refused") {
    return <ForbiddenState title={t("sales.access.title")} body={t("sales.access.importOrders")} />;
  }
  return <>{children}</>;
}
